/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { Heading1, Paragraph, LoadingSpinner } from '@motili/ui';
import { ReactElement, useEffect } from 'react';
import { styled } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import DocumentCard from './DocumentCard';
import { useAppDispatch, useAppSelector } from '../../../common/redux/hooks';
import CenteredContainer from '../../../components/layout/CenteredContainer';
import BottomButtonNavigation from '../../../navigation/BottomButtonNavigation';
import { setUUID } from '../../../common/redux/ducks/contractorRegistrationForm';
import { DOCUMENT_TYPES } from '../../../common/types';

interface DocumentsProps {
    handleBackClick: () => void;
    handleNextClick: () => void;
    handleUploadNavigationClick: (
        fileName: string,
        label: string
    ) => () => void;
    busy: boolean;
}

export default function Documents({
    handleBackClick,
    handleNextClick,
    handleUploadNavigationClick,
    busy,
}: DocumentsProps): ReactElement {
    const dispatch = useAppDispatch();
    const contractor = useAppSelector(
        ({ contractorRegistration }) => contractorRegistration.contractor
    );
    const storedUUID = useAppSelector(
        ({ contractorRegistration }) => contractorRegistration.UUID
    );
    const { documents, registerForLaborJobs } = contractor;

    const LABOR_REQUIRED_DOCS: DOCUMENT_TYPES[] = [
        DOCUMENT_TYPES.certificateOfLiability,
        DOCUMENT_TYPES.epaCertification,
        DOCUMENT_TYPES.secretaryOfStateRegistration,
        DOCUMENT_TYPES.w9,
        DOCUMENT_TYPES.tradeLicenses,
    ];

    const NON_LABOR_REQUIRED_DOCS: DOCUMENT_TYPES[] = [
        DOCUMENT_TYPES.epaCertification,
        DOCUMENT_TYPES.secretaryOfStateRegistration,
        DOCUMENT_TYPES.w9,
    ];

    const requiredDocs = registerForLaborJobs
        ? LABOR_REQUIRED_DOCS
        : NON_LABOR_REQUIRED_DOCS;

    const allRequiredDocsUploaded = requiredDocs.every(
        key => documents[key]?.length > 0
    );

    const isDisabled = !allRequiredDocsUploaded || busy;

    useEffect(() => {
        if (!storedUUID) {
            dispatch(setUUID(uuidv4()));
        }
    }, [dispatch, storedUUID]);

    return (
        <CenteredContainer>
            {busy && (
                <LoaderOverlay>
                    <LoadingSpinner fullscreen busy={busy} />
                </LoaderOverlay>
            )}
            <Heading1>Documents & Agreements</Heading1>
            <Paragraph>
                The documents below are required prior to working with us. If
                you do not have these documents on-hand , you can save your
                progress and return when you’ve completed this information.
            </Paragraph>
            <DocumentCard
                label='EPA Certification'
                name='epaCertification'
                handleClick={handleUploadNavigationClick(
                    'epaCertification',
                    'EPA Certification'
                )}
                icon='fas fa-globe-americas'
            />
            <DocumentCard
                label='Secretary of State Registration Certification'
                name='secretaryOfStateRegistration'
                handleClick={handleUploadNavigationClick(
                    'secretaryOfStateRegistration',
                    'Secretary of State Registration Certification'
                )}
                icon='fas fa-landmark'
            />
            <DocumentCard
                label='W-9 Form'
                name='w9'
                handleClick={handleUploadNavigationClick('w9', 'W-9 Form')}
                icon='fa fa-file-o'
                subText='Business name must match Secretary of State Registration business name.'
            />
            {registerForLaborJobs && (
                <>
                    <DocumentCard
                        label='Trade Licenses'
                        name='tradeLicenses'
                        handleClick={handleUploadNavigationClick(
                            'tradeLicenses',
                            'Trade Licenses'
                        )}
                        icon='fa fa-award'
                    />
                    <DocumentCard
                        label='Certificate of Insurance'
                        name='certificateOfLiability'
                        handleClick={handleUploadNavigationClick(
                            'certificateOfLiability',
                            'Certificate of Insurance'
                        )}
                        icon='fa fa-user-shield'
                    />
                </>
            )}
            <DocumentCard
                label='Tax Sales Certificate'
                name='taxSalesCertificate'
                handleClick={handleUploadNavigationClick(
                    'taxSalesCertificate',
                    'Tax Sales Certificate'
                )}
                icon='fa fa-usd'
                isRequired={false}
            />
            <BottomButtonNavigation
                onBackClick={handleBackClick}
                onNextClick={handleNextClick}
                nextButtonText='Submit'
                nextDisabled={isDisabled}
            />
        </CenteredContainer>
    );
}

const LoaderOverlay = styled.div`
    top: 0;
    left: 0;
    position: fixed;
    width: 100%;
    height: 100%;
    background-color: gray;
    opacity: 0.3;
    align-items: center;
    justify-content: center;
    display: flex;
`;
